<template>
    <div>
        <v-row dense>
            <v-col cols="12">
                <div class="text-subtitle-1">{{ $t('product.info.suggest_extra_service') }}:</div>
                <v-radio-group v-model="formData.extraServiceApplicationType" density="compact" hide-details>
                    <v-radio :label="$t('product.info.always')" value="ALWAYS" data-cy="always" />
                    <v-radio
                        :label="$t('product.info.different_locations')"
                        value="DIFFERENT_OFFICES"
                        data-cy="different-offices"
                    />
                    <v-radio
                        :label="$t('product.info.combinations')"
                        value="OFFICES_COMBINATION"
                        data-cy="offices-combinations"
                    />
                </v-radio-group>
            </v-col>
        </v-row>
        <v-row v-if="formData.extraServiceApplicationType === 'OFFICES_COMBINATION'" dense class="pb-6">
            <v-col cols="12" md="8">
                <div
                    v-if="!formData.officeCombinations?.length"
                    class="text-subtitle-2 pt-1 text-red-darken-4 font-weight-regular mb-2"
                >
                    {{ $t('offices_required') }}
                </div>
                <v-data-table
                    :headers="useHeaders"
                    :items="formData.officeCombinations"
                    disable-sort
                    density="compact"
                    class="elevation-1 mb-2"
                    data-cy="table"
                    :hide-default-footer="formData.officeCombinations?.length < 10"
                >
                    <template v-slot:item.pickupOfficeId="{item}">
                        {{ item?.pickupOfficeId && useOffices?.length ? getName(item.pickupOfficeId) : '' }}
                    </template>
                    <template v-slot:item.dropoffOfficeId="{item}">
                        {{ item?.dropoffOfficeId && useOffices?.length ? getName(item.dropoffOfficeId) : '' }}
                    </template>
                    <template v-slot:item.action="{item}">
                        <v-btn
                            variant="text"
                            class="text-caption"
                            :data-cy="`delete-office-${item}`"
                            density="compact"
                            @click="deleteOffice(item)"
                        >
                            {{ $t('delete') }}
                        </v-btn>
                    </template>
                </v-data-table>
                <v-btn data-cy="add-office" :disabled="!availableOffices?.length" @click="isShowModal = true">
                    {{ $t('add') }}
                </v-btn>
            </v-col>
        </v-row>
        <ModalWrapper v-model="isShowModal" :max-width="1200">
            <template v-slot:title>
                {{ $t('select_car_hire_offices') }}
            </template>
            <template v-slot:content>
                <v-row dense>
                    <v-col cols="12" md="6">
                        <div class="text-subtitle-1">{{ $t('pick_up_offices') }}:</div>
                        <v-data-table
                            v-model="selectedPickUpOffices"
                            :headers="useSelectHeaders"
                            :items="availableOffices"
                            disable-sort
                            density="compact"
                            class="elevation-1 table-server-selected"
                            data-cy="table-pick-up-offices"
                            :items-per-page="-1"
                            hide-default-footer
                            show-select
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <div class="text-subtitle-1">{{ $t('drop_off_offices') }}:</div>
                        <v-data-table
                            v-model="selectedDropOffOffices"
                            :headers="useSelectHeaders"
                            :items="availableOffices"
                            disable-sort
                            density="compact"
                            class="elevation-1 table-server-selected"
                            data-cy="table-drop-off"
                            :items-per-page="-1"
                            hide-default-footer
                            show-select
                        />
                    </v-col>
                </v-row>
            </template>
            <template v-slot:btns>
                <v-btn
                    data-cy="save"
                    :disabled="!selectedPickUpOffices?.length || !selectedDropOffOffices?.length"
                    variant="flat"
                    @click="addOffices()"
                >
                    {{ $t('apply') }}
                </v-btn>
            </template>
        </ModalWrapper>
    </div>
</template>

<script setup lang="ts">
    import {useFullContract, useOffices} from '~/composables/form-helpers'

    const availableOffices = ref([])
    const selectedPickUpOffices = ref([])
    const selectedDropOffOffices = ref([])
    const isShowModal = ref<boolean>(false)

    const formData = defineModel<any>()

    const {t} = useI18n()

    onMounted(async () => {
        await supplierStore.loadOffices()
        prepareOffices()
    })

    watch(useFullContract, async () => {
        prepareOffices()
    })

    const prepareOffices = () => {
        const officeIds = useFullContract.value.officeIds || []
        availableOffices.value = officeIds?.map(office => useOffices.value.find(({id}) => id === office))
    }

    const useHeaders = computed(() => {
        return [
            {title: t('pick_up_offices'), value: 'pickupOfficeId'},
            {title: t('drop_off_offices'), value: 'dropoffOfficeId'},
            {title: '', value: 'action', minWidth: '120px', width: '10%'},
        ]
    })

    const useSelectHeaders = computed(() => {
        return [
            {title: t('name'), value: 'name'},
            {title: t('country'), value: 'countryName'},
            {title: t('city'), value: 'cityName'},
        ]
    })

    const getName = item => {
        const currentItem = useOffices.value.find(({id}) => id === item)
        return currentItem && `${currentItem.name} (${currentItem.countryName}, ${currentItem.cityName})`
    }

    const getUniqueCombinations = (selectedPickup, selectedDropoff, existingCombinations) => {
        const uniqueCombinations = []
        for (const pickup of selectedPickup) {
            for (const dropoff of selectedDropoff) {
                const newCombination = {pickupOfficeId: pickup, dropoffOfficeId: dropoff}
                if (
                    !existingCombinations.some(
                        existing => existing.pickupOfficeId === pickup && existing.dropoffOfficeId === dropoff
                    )
                ) {
                    uniqueCombinations.push(newCombination)
                }
            }
        }
        return uniqueCombinations
    }

    const addOffices = (): void => {
        isShowModal.value = false
        const uniqueCombinations = getUniqueCombinations(
            selectedPickUpOffices.value,
            selectedDropOffOffices.value,
            formData.value.officeCombinations
        )
        formData.value.officeCombinations = [...uniqueCombinations, ...formData.value.officeCombinations]
        selectedPickUpOffices.value = []
        selectedDropOffOffices.value = []
    }

    const deleteOffice = (item): void => {
        formData.value.officeCombinations = formData.value.officeCombinations.filter(
            ({dropoffOfficeId, pickupOfficeId}) =>
                pickupOfficeId !== item.pickupOfficeId || dropoffOfficeId !== item.dropoffOfficeId
        )
    }
</script>
